import React from 'react';
import TextArea from 'react-textarea-autosize';

import {
  extensionStyles,
  inputAndLabelWrapper,
  inputCss,
  labelCss,
  styles,
} from '~src/ui/text-input.style';

export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  rightElement?: (setFocus: React.Dispatch<React.SetStateAction<boolean>>) => React.ReactNode;
  error?: string | boolean;
  inputRef?: React.RefObject<HTMLInputElement> | React.ForwardedRef<HTMLInputElement>;
  label?: string;
  raiseLabelStyle?: React.CSSProperties;
  textarea?: boolean;
  wrapperStyle?: React.CSSProperties;
  warning?: boolean;
  dataTest?: string;
  isStreetAndNumberField?: boolean;
  isDisabled?: boolean;
}

export const TextInput: React.FC<TextInputProps> = ({
  rightElement,
  error,
  warning,
  inputRef,
  label,
  raiseLabelStyle,
  textarea,
  wrapperStyle,
  dataTest,
  isStreetAndNumberField = false,
  isDisabled,
  ...props
}) => {
  const [focused, setFocused] = React.useState<boolean>(false);

  // FIXME: this is broken...
  const value = (props.value as string) || '';

  const raiseLabel = focused || props.value;

  const inputProps = {
    'aria-label': label,
    css: inputCss(textarea, isDisabled),
    ...props,
    value,
  };

  const wordCountElement =
    props.maxLength !== undefined && raiseLabel ? (
      <span css={styles.wordCount}>
        {value.length}/{props.maxLength}
      </span>
    ) : undefined;

  const labelText = typeof error === 'string' ? error : label;

  /*
   * Hack: It is problematic to always elect a global 'id' in order to connect
   * the input to label. We better hide the visual label from aria and add aria
   * prop to the input.
   */
  const labelElement = (
    <div
      aria-hidden={error ? 'false' : 'true'}
      css={labelCss(Boolean(raiseLabel), Boolean(error), warning, isDisabled)}
      style={raiseLabel ? raiseLabelStyle : undefined}
    >
      <span role={error ? 'alert' : undefined}>{labelText}</span>
      {wordCountElement}
    </div>
  );

  const inputElement = textarea ? (
    // @ts-ignore wrong ref type for textarea
    <TextArea {...inputProps} disabled={isDisabled} ref={inputRef} />
  ) : (
    <input {...inputProps} disabled={isDisabled} ref={inputRef} data-test={dataTest} />
  );

  const rightExtensionElement = () => {
    if (rightElement) {
      if (isStreetAndNumberField) return <div>{rightElement?.(setFocused)}</div>;
      return <div css={extensionStyles}>{rightElement?.(setFocused)}</div>;
    }
    return null;
  };

  return (
    <div
      css={styles.wrapper(focused, Boolean(error), Boolean(warning), textarea, isDisabled)}
      style={wrapperStyle}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <div css={inputAndLabelWrapper}>
        {labelElement}
        {inputElement}
      </div>
      {!isDisabled && rightExtensionElement()}
    </div>
  );
};
